import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CourseBanner from '../../components/ChapterPages/CourseBanner';
import CourseProgressBox from '../../components/ChapterPages/CourseProgressBox';
import ChapterItem from '../../components/ChapterPages/ChapterItem';
import '../../css/BasicContentPage.css';
import { useStudentProgress } from '../../state/StudentProgressContext';
import { useData } from '../../state/DataContext';
import { padSectionNumber, slugifySectionName } from '../../utils/slugify';

function AdvancedContentPage() {
  const { advancedData, loading: dataLoading, error: dataError } = useData();
  const { studentSections, loading: progressLoading, error: progressError } = useStudentProgress();
  const [openIndices, setOpenIndices] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  const chaptersWithProgress = useMemo(() => {
    const studentProgress = Object.fromEntries(
      (studentSections || []).map(s => [s.sectionId, s.status])
    );
    return (advancedData || []).map(chapter => ({
      ...chapter,
      sections: chapter.sections.map(section => {
        const status = studentProgress[section.section_id] || "in_progress";
        return {
          ...section,
          completed: status === "completed",
          claimed: section.type === "bonus" ? status === "completed" : undefined
        };
      })
    }));
  }, [advancedData, studentSections]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openIdx = params.get('open');
    const chapterId = params.get('chapterId');
    if (openIdx !== null && !isNaN(openIdx)) {
      setOpenIndices([parseInt(openIdx, 10)]);
      return;
    }
    if (chapterId) {
      const idx = chaptersWithProgress.findIndex(c => c.id === String(chapterId));
      if (idx !== -1) setOpenIndices([idx]);
    }
  }, [location.search, chaptersWithProgress]);

  const handleStartCourseClick = () => {
    if (chaptersWithProgress.length > 0 && chaptersWithProgress[0].sections.length > 0) {
      const firstSection = chaptersWithProgress[0].sections.find(s => s.type === 'section');
      if (firstSection) {
        const paddedNum = padSectionNumber(1); // Assuming first section is always section 1
        const slug = slugifySectionName(firstSection.name);
        navigate(`/advanced/${paddedNum}-${slug}`);
      }
    }
  };

  const handleToggle = (idx) => {
    setOpenIndices((prev) =>
      prev.includes(idx)
        ? prev.filter(i => i !== idx)
        : [...prev, idx]
    );
  };

  const allProgressItems = chaptersWithProgress.flatMap(chapter =>
    (chapter.sections || []).filter(s => s.type === "section" || s.type === "bonus")
  );
  const firstIncompleteGlobalIdx = allProgressItems.findIndex(item => !item.completed);

  let globalProgressIndex = 0;
  let globalSectionNumber = 1;

  if (dataLoading || progressLoading) return <div>Loading...</div>;
  if (dataError || progressError) return <div>{dataError || progressError}</div>;

  return (
    <div>
      <CourseBanner
        title="Advanced"
        description="Master advanced VTS communication, including complex scenarios, advanced message structures, and expert-level prowords for maritime operations."
        onButtonClick={handleStartCourseClick}
      />
      <div className="selection-banner">
        <div className="center-content-row">
          <div className="chapter-selector">
            <ol className="chapter-list">
              {chaptersWithProgress.map((chapter, idx) => {
                const chapterProgressItems = (chapter.sections || []).filter(s => s.type === "section" || s.type === "bonus");
                const currentProgressStart = globalProgressIndex;
                const currentSectionStart = globalSectionNumber;
                const numSections = (chapter.sections || []).filter(s => s.type === "section").length;
                globalProgressIndex += chapterProgressItems.length;
                globalSectionNumber += numSections;
                return (
                  <ChapterItem
                    key={idx}
                    index={idx}
                    chapter={chapter}
                    isOpen={openIndices.includes(idx)}
                    onToggle={handleToggle}
                    basePath="advanced"
                    progressStartIndex={currentProgressStart}
                    sectionStartNumber={currentSectionStart}
                    firstIncompleteGlobalIdx={firstIncompleteGlobalIdx}
                    courseType="advanced"
                  />
                );
              })}
            </ol>
          </div>
          <CourseProgressBox chapters={chaptersWithProgress} />
        </div>
      </div>
    </div>
  );
}

export default AdvancedContentPage;